import {useEffect, useState} from 'react';
import {retryIfFails} from '@/utils/helpers/api';

/**
 * @description Loads the Exponea SDK from the `window` object.
 * - It checks if `window.exponea` is available and if it has the `start` method.
 * - If the SDK is not available, it rejects the promise with an error.
 * - If the SDK is available, it initializes it with default options and resolves the promise with the
 * @param {Window} window
 * @return {Promise<object>} A promise that resolves to the `window.exponea` object.
 */
const loadExponeaSdk = (window: Window): Promise<NonNullable<Window['exponea']>> =>
  new Promise((resolve, reject) => {
    /**
     * `window.exponea?.start` is being used as a telltale to check if the SDK is loaded
     */
    if (!window.exponea?.start) {
      reject(new Error('Exponea is not available on the window object'));
    } else {
      window.exponea.start({
        track: {
          auto: true,
        },
        ping: {
          enabled: true,
        },
        spa_reloading: true,
      });
      resolve(window.exponea);
    }
  });

/**
 * @description custom hook to access the `window.exponea` object.
 * It checks if `window.exponea` exists and returns it if available.
 * If `window.exponea` is not defined, it returns `null`.
 * This hook is useful for components that need to interact with the Exponea service
 * that is initialized at _app.tsx level;
 * @return {object} `exponea`, loading and error status.
 */
const useExponea = (): {
  exponea: NonNullable<typeof window.exponea> | null;
  error: null | string;
  isLoading: boolean;
} => {
  const [exponea, setExponea] = useState<NonNullable<typeof window.exponea> | null>(null);
  const [exponeaError, setExponeaError] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadExponea = async () => {
      setIsLoading(true);
      setExponeaError(null);
      try {
        const exponeaSdk = await retryIfFails(() => loadExponeaSdk(window), 3);
        /**
         * Check if component is still mounted
         */
        if (!isMounted) return;
        setIsLoading(false);
        setExponea(exponeaSdk);
      } catch (error) {
        /**
         * Check if component is still mounted
         */
        if (!isMounted) return;
        const errorMessage = error instanceof Error ? error.message : 'Error loading exponea SDK';
        setIsLoading(false);
        setExponeaError(errorMessage);
      }
    };

    loadExponea();

    /**
     * Cleanup function to set `isMounted` to false when the component unmounts
     */
    return () => {
      isMounted = false;
    };
  }, []);

  return {exponea, error: exponeaError, isLoading};
};

export default useExponea;
